// src/hooks/useTableHeader.tsx
import React, { useEffect, useMemo, useState } from "react";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { Search } from "lucide-react";
import { Filter, SimpleFilter } from "../types/filter";
import { useDebounce } from "./useDebounce";

export type SearchField = { field: string; operator?: SimpleFilter["operator"] };

type UseTableHeaderOptions = {
  title?: string;
  placeholder?: string;
  searchFields?: SearchField[];
  debounceMs?: number;
  actions?: React.ReactNode;
  initialValue?: string | null;
  onFilterChange?: (filter: Filter | null) => void;
  className?: string;
};

export const useTableHeader = (opts: UseTableHeaderOptions = {}) => {
  const {
    title = "Liste",
    placeholder = "Ara...",
    searchFields = [{ field: "name", operator: "contains" }],
    debounceMs = 350,
    actions = null,
    initialValue = null,
    onFilterChange,
    className = "",
  } = opts;

  const [globalFilterValue, setGlobalFilterValue] = useState<string | null>(initialValue);
  const debouncedSearch = useDebounce(globalFilterValue, debounceMs);
  const [dynamicFilter, setDynamicFilter] = useState<Filter | null>(null);

  useEffect(() => {
    if (!debouncedSearch) {
      setDynamicFilter(null);
      onFilterChange?.(null);
      return;
    }

    const filters = searchFields.map((sf) => ({
      field: sf.field,
      operator: sf.operator ?? ("contains" as const),
      value: debouncedSearch,
    }));

    const built: Filter = {
      logic: filters.length > 1 ? "or" : "and",
      filters,
    };

    setDynamicFilter(built);
    onFilterChange?.(built);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, JSON.stringify(searchFields)]);

  const header = useMemo(() => {
    return (
      <div className={`flex flex-col lg:flex-row lg:justify-between lg:items-center gap-3 lg:gap-4 ${className}`}>
        {/* Başlık */}
        <h2 className="text-xl md:text-2xl font-bold text shrink-0">{title}</h2>

        {/* 🌟 Arama ve Aksiyonlar (Sığmadığında alt satıra geçecek esnek yapı) */}
        <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto justify-start lg:justify-end flex-1">
          
          {/* Arama Inputu Alanı */}
          <div className="flex-1 min-w-[200px] max-w-full lg:max-w-[300px] xl:max-w-[400px]">
            <IconField className="w-full left">
              <InputIcon>
                <Search size={18} />
              </InputIcon>
              
              <InputText
                className="input w-full pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition duration-150"
                value={globalFilterValue ?? ""}
                onChange={(e: any) => setGlobalFilterValue(e.target.value)}
                placeholder={placeholder}
              />
            </IconField>
          </div>

          {/* Aksiyon Butonları (Hamburger Menü vb.) */}
          {actions && (
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              {actions}
            </div>
          )}
        </div>
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalFilterValue, actions, title, placeholder, className]);

  return {
    header,
    dynamicFilter,
    globalFilterValue,
    setGlobalFilterValue,
  } as const;
};